/**
 * PocketFiler - Changelog Page
 * Public "What's New" page showing recent product updates.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Shield, Zap, Palette, Languages, Users } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useTranslation } from '../i18n';


const CHANGELOG = [
  {
    version: 'v1.5',
    date: '2026-04-18',
    tagline: 'Polished for launch',
    updates: [
      { type: 'feature', icon: Users, text: 'New role-based onboarding — personalized first-action flow for 6 user types' },
      { type: 'feature', icon: Sparkles, text: 'Setup checklist widget on the dashboard tracks your progress' },
      { type: 'feature', icon: Zap, text: 'Usage meters + smart upgrade prompts when you hit plan limits' },
      { type: 'feature', icon: Palette, text: 'Refreshed pricing page — feature comparison table + FAQ + money-back guarantee' },
    ],
  },
  {
    version: 'v1.4',
    date: '2026-04-17',
    tagline: 'Feedback first',
    updates: [
      { type: 'feature', icon: Sparkles, text: 'In-app feedback widget — report bugs, request features, or leave general feedback from any page' },
      { type: 'feature', icon: Sparkles, text: 'Public signup is open — invite your clients and team today' },
      { type: 'feature', icon: Zap, text: 'Upgraded to Claude Opus 4.7 for premium AI tasks — 3x better quality, same cost' },
    ],
  },
  {
    version: 'v1.3',
    date: '2026-04-16',
    tagline: 'Enterprise-grade security',
    updates: [
      { type: 'security', icon: Shield, text: 'Two-factor authentication (TOTP) with backup codes' },
      { type: 'security', icon: Shield, text: 'Token refresh + active session management — see and revoke sessions anywhere' },
      { type: 'security', icon: Shield, text: 'Audit trail — full login + activity history in Settings → Security' },
      { type: 'security', icon: Shield, text: 'Account lockout protection after failed login attempts' },
      { type: 'security', icon: Shield, text: 'File upload validation — types whitelisted, size enforced, path traversal blocked' },
    ],
  },
  {
    version: 'v1.2',
    date: '2026-04-15',
    tagline: 'Documents, translated',
    updates: [
      { type: 'feature', icon: Sparkles, text: 'Create Document page — rich text editor with AI writing assistant' },
      { type: 'feature', icon: Languages, text: 'Translate any document to 12 languages with one click' },
      { type: 'feature', icon: Sparkles, text: 'AI Contract Templates — NDA, SOW, MSA, Contractor Agreement, LOI (6 templates)' },
      { type: 'feature', icon: Zap, text: 'Print + PDF export everywhere — clean formatting, branded invoices' },
      { type: 'feature', icon: Sparkles, text: 'Recurring meetings — schedule weekly/biweekly/monthly meetings that auto-generate' },
    ],
  },
  {
    version: 'v1.1',
    date: '2026-04-14',
    tagline: 'Multi-lingual by default',
    updates: [
      { type: 'feature', icon: Languages, text: '12 languages fully translated across all core pages' },
      { type: 'feature', icon: Languages, text: 'Added Vietnamese language (Tier 1 support)' },
      { type: 'feature', icon: Zap, text: 'AI Briefing widget — smart summary of your day on the dashboard' },
      { type: 'feature', icon: Zap, text: 'Zapier integration — 3 triggers + 2 actions, published to Zapier platform' },
      { type: 'feature', icon: Zap, text: 'Google Calendar integration — OAuth sync + event management' },
    ],
  },
];


const TYPE_COLORS = {
  feature: 'bg-primary/10 text-primary border-primary/20',
  security: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900',
  fix: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900',
  improvement: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900',
};


export default function ChangelogPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background">
      {/* Simple header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            {t('changelog.backHome') || 'Back to home'}
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">PocketFiler Changelog</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="mb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Sparkles className="h-3 w-3" />
            {t('changelog.tagline') || 'What\'s New'}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-3">{t('changelog.title') || 'Changelog'}</h1>
          <p className="text-lg text-muted-foreground">
            {t('changelog.subtitle') || 'Updates, improvements, and new features. Ship fast, ship often.'}
          </p>
        </div>

        <div className="space-y-10">
          {CHANGELOG.map((release, i) => (
            <article key={i} className="relative">
              {/* Version header */}
              <div className="flex items-baseline gap-3 mb-2">
                <h2 className="text-2xl font-bold">{release.version}</h2>
                <time className="text-sm text-muted-foreground">
                  {new Date(release.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
              </div>
              <p className="text-base text-muted-foreground mb-5">{release.tagline}</p>

              {/* Updates list */}
              <ul className="space-y-3">
                {release.updates.map((update, j) => {
                  const Icon = update.icon;
                  return (
                    <li key={j} className="flex items-start gap-3">
                      <div className={`mt-0.5 h-8 w-8 rounded-lg border flex items-center justify-center shrink-0 ${TYPE_COLORS[update.type] || TYPE_COLORS.feature}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 pt-1">
                        <p className="text-sm leading-relaxed">{update.text}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-16 text-center border-t pt-10">
          <h3 className="text-xl font-semibold mb-2">{t('changelog.haveIdea') || 'Got an idea or feedback?'}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('changelog.feedbackCta') || 'Use the feedback button in the app, or email us.'}
          </p>
          <a href="mailto:info@pocketfiler.com" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors">
            {t('changelog.contactUs') || 'Contact us'}
          </a>
        </div>
      </div>
    </div>
  );
}
